'use client'

import React, { useEffect, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "@/lib/utils";
import { Input } from "../ui/input";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetPalletsForTestStackQuery, useUpdatePalletsForTestStackMutation } from "@/redux/slices/preship/preship-api-slice";
import { useFormik } from "formik";

import * as Yup from 'yup';
import Footer from "../footer";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import EditBanner from "../common-components/EditBanner";
import { StackTestLayoutService } from "@/services/StackTestLayoutService";
import { Skeleton } from "@/components/ui/skeleton";
const holdDuration = [
  'Week 1',
  "Week 2",
  "Week 3",
  "Week 4"
];

const stackPatterns = [
  { name: "2-HIGH COLUMN", minPallets: 6 },
  { name: "2-HIGH PYRAMID", minPallets: 6 },
  { name: "3-HIGH COLUMN", minPallets: 12 },
  { name: "3-HIGH PYRAMID", minPallets: 10 },
  { name: "4-HIGH COLUMN", minPallets: 16 },
  { name: "4-HIGH PYRAMID", minPallets: 14 },
];

const validationSchema = Yup.object().shape({
  pallets: Yup.array().of(
    Yup.object().shape({
      stackTestLayoutWeek: Yup.string(),
      stackPattern: Yup.string(),
    })
  ),
});

interface StackTestLayoutProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
}
//makr this component as read only
export default function StackTestLayout({
  // projectId,
  isOffline,
  onOfflineSubmit
}: Readonly<StackTestLayoutProps>) {
  // console.log(projectId,'projectId inside stack test layout')
  const { projectId } = useSelector((state: RootState) => state.define)
  const { data: pallets, isLoading, error } = useGetPalletsForTestStackQuery(projectId, { refetchOnMountOrArgChange: true });
  const [updatePallets, { isLoading: updatePalletLoading }] = useUpdatePalletsForTestStackMutation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false)
  const [offlinePallets, setOfflinePallets] = useState<any>(null)

  console.log(error, 'error in etting pallets', pallets)
  const formik = useFormik<any>({
    initialValues: {
      pallets: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      if (isOffline) {
        try {
          await StackTestLayoutService.updatePalletsForTestStack(Number(projectId), values.pallets)
          showSuccessToast("Pallets updated successfully");
          setHasUnsavedChanges(false);
          return;
        } catch (error) {
          showErrorToast("Error updating pallets");
          console.error("Error updating pallets:", error);
        }
      }

      try {
        await updatePallets({ projectId, pallets: values.pallets });
        showSuccessToast("Pallets updated successfully");
        setHasUnsavedChanges(false);
      } catch (error) {
        showErrorToast("Error updating pallets");
        console.error("Error updating pallets:", error);
      }
    },
  });
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await StackTestLayoutService.getPalletsForTestStack(Number(projectId))
          setOfflinePallets(res)
          formik.setValues({
            pallets: res.map((pallet: any) => ({
              id: pallet.id,
              name: pallet.name,
              stackTestLayoutWeek: pallet.stackTestLayoutWeek || "",
              stackPattern: pallet.stackPattern || "",
              lpnCount: pallet.lpnCount,
              recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || "",
            })),
          });
        } catch (error) {
          console.error('Error fetching stack test layout data:', error);
        }
      }
      getData()
    }
  }, [])
  useEffect(() => {
    if (pallets && !isOffline) {
      formik.setValues({
        pallets: pallets.map((pallet: any) => ({
          id: pallet.id,
          name: pallet.name,
          stackTestLayoutWeek: pallet.stackTestLayoutWeek || "",
          stackPattern: pallet.stackPattern || "",
          lpnCount: pallet.lpnCount,
          recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || "",
        })),
      });
    } else if (isOffline && offlinePallets) {
      formik.setValues({
        pallets: offlinePallets.map((pallet: any) => ({
          id: pallet.id,
          name: pallet.name,
          stackTestLayoutWeek: pallet.stackTestLayoutWeek || "",
          stackPattern: pallet.stackPattern || "",
          lpnCount: pallet.lpnCount,
          recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || "",
        })),
      });
    }
  }, [pallets, isOffline, offlinePallets]);

  useEffect(() => {
    const hasChanges = JSON.stringify(formik.initialValues) !== JSON.stringify(formik.values);
    setHasUnsavedChanges(hasChanges);
  }, [formik.values, formik.initialValues]);

  const handleSubmit = useCallback(async () => {
    await formik.submitForm();
  }, [formik]);
  useEffect(() => {
    if (isOffline) {
      setIsEditing(true)
    }
  }, [isOffline])
  if (isLoading) return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Header skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Form fields skeleton */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>

      {/* Table skeleton */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-4 p-3">
            {Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="grid grid-cols-4 gap-2 p-2 border-b last:border-0">
              {Array(4).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons skeleton */}
      <div className="flex justify-end space-x-3 mt-6">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  );
  return (
    <div className="border-0 rounded-md overflow-x-scroll py-2 px-4 h-[calc(100vh-140px)] overflow-y-auto">
      <div className=" my-1">
        {!isOffline && <EditBanner
          stage={'stackLayout'}
          palletId={1}
          component={'Stack Test Layout'}
          onEditStateChange={setIsEditing}
        />}
      </div>
      <form id={`stack-layout-test`} className={`${isEditing ? "" : "pointer-events-none"}`} onSubmit={formik.handleSubmit}>
        <Table className="border-collapse">
          <TableHeader>
            <TableRow>
              {[
                "Pallet Name",
                "Stack Hold Duration",
                "Stack Pattern",
                "Number of Pallets used for stack testing",
                "Recommended minimum number of pallets for stack testing",
              ].map((head, headIndex) => (
                <TableHead
                  key={`${headIndex}-${head}`}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.pallets.map((pallet: any, index: number) => (
              <TableRow key={pallet.id}>
                <TableCell className={cn("border border-gray-300 bg-states-disabled p-0")}>
                  <Input
                    className="w-full h-9 border-none"
                    value={pallet.name}
                    readOnly
                  />
                </TableCell>
                <TableCell className={cn("border border-gray-300 p-0")}>
                  <Select
                    onValueChange={(value) => formik.setFieldValue(`pallets.${index}.stackTestLayoutWeek`, value)}
                    value={pallet.stackTestLayoutWeek}
                  >
                    <SelectTrigger className="w-full h-9 border-none">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {holdDuration.map((week) => (
                        <SelectItem key={week} value={week}>
                          {week}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className={cn("border border-gray-300 p-0")}>
                  <Select
                    onValueChange={(value) => {
                      formik.setFieldValue(`pallets.${index}.stackPattern`, value);
                      const minPallets = stackPatterns.find(p => p.name === value)?.minPallets || "";
                      formik.setFieldValue(`pallets.${index}.recommendedMinimumPallets`, minPallets);
                    }}
                    value={pallet.stackPattern}
                  >
                    <SelectTrigger className="w-full h-9 border-none">
                      <SelectValue placeholder="Select pattern" />
                    </SelectTrigger>
                    <SelectContent>
                      {stackPatterns.map((pattern) => (
                        <SelectItem key={pattern.name} value={pattern.name}>
                          {pattern.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell
                  className={cn(
                    "border border-gray-300 p-0",
                    pallet.stackPattern
                    //  && (
                    //   pallet.lpnCount < pallet.recommendedMinimumPallets
                    //     ? "bg-bgRed text-white"
                    //     : "bg-states-success text-white"
                    // )
                  )}
                >
                  <Input
                    className="w-full h-9 border-none bg-transparent"
                    value={pallet.lpnCount}
                    readOnly
                  />
                </TableCell>
                <TableCell className={cn("border border-gray-300 bg-states-disabled p-0")}>
                  <Input
                    className="w-full h-9 border-none"
                    value={pallet.recommendedMinimumPallets}
                    readOnly
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StackLayoutPatterns />

      </form>
      <Footer
        layout={false}
        isLoading={updatePalletLoading}
        onSubmit={handleSubmit}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  );
};

const StackLayoutPatterns: React.FC = () => {
  const patterns = [
    { name: '2-HIGH COLUMN', image: "/stackLayoutImages/2highColumn.png" },
    { name: '2-HIGH PYRAMID', image: "/stackLayoutImages/2highPyramid.png" },
    { name: '3-HIGH COLUMN', image: "/stackLayoutImages/3highColumn.png" },
    { name: '3-HIGH PYRAMID', image: "/stackLayoutImages/3highPyramid.png" },
    { name: '4-HIGH COLUMN', image: "/stackLayoutImages/4highColumn.png" },
    { name: '4-HIGH PYRAMID', image: "/stackLayoutImages/4highPyramid.png" },
  ];

  return (
    <div className="mt-8 border border-gray-300 rounded-md overflow-hidden mb-[100px]">
      <table className="w-full">
        <thead>
          <tr>
            <th className="w-1/3 bg-gray-100 text-left p-2 border-b border-r border-gray-300">Stacking Pattern</th>
            <th className="w-2/3 bg-gray-100 text-left p-2 border-b border-gray-300">Layout</th>
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern) => (
            <tr key={pattern.name} className="border-b border-gray-300 last:border-b-0">
              <td className="p-2 border-r border-gray-300">{pattern.name}</td>
              <td className="p-2">
                <img
                  src={pattern.image}
                  alt={`${pattern.name} layout`}
                  className="mx-auto"

                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
